import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "web/react/emo/button";
import { ProductCardImageEmo } from "web/react/emo/product-image";
import { Price } from "web/react/emo/product-price";
import { Retailer } from "web/react/emo/retailer";
import { Text } from "web/react/emo/text";
import { View } from "web/react/emo/view";
import { useAboveFold } from "web/react/hooks/use-above-fold/use-above-fold";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import rothko from "web/script/modules/rothko";
import { BrandAdSerializer } from "web/types/serializers";
import styles from "./brand-ad-container.module.css";

const ENDPOINT = "modules/brand_ads";

export function BrandAdContainer({
    jpg_banner_image: jpgBannerImage,
    link,
    click_url: clickUrl,
    impression_url: impressionUrl,
    mobile_link: mobileLink,
    jpg_mpu_image: jpgMpuImage,
    product_image: productImage,
    product_link: productLink,
    product_description: productDescription,
    product_price: productPrice,
    partner,
    retailer_network: retailerNetwork,
    campaign_id: campaignId,
    flight_id: flightId,
    creative_id: creativeId,
    ad_id: adId,
}: BrandAdSerializer): null | JSX.Element {
    const containerRef = useRef<HTMLDivElement>(null);

    const { isAboveFold, isVisible } = useAboveFold(containerRef);
    const [isLoaded, setIsLoaded] = useState(false);

    // as final insurance, don't accidentally send non-booleans to analytics customData

    const sendKevelTracking = useCallback(
        (isClick: boolean): void => {
            const type = "pba";
            const url = isClick ? clickUrl : impressionUrl;
            if (url) {
                const clicked = isClick ? "true" : "false";
                rothko.fetch(ENDPOINT, { url, clicked, type }, { cache: false });
            }
        },
        [clickUrl, impressionUrl]
    );

    const sendAnalyticsEvent = useCallback(
        (action, area) => {
            analytics.event(
                "brand_ads", // category
                action,
                "",
                action !== "click", // nonInteraction
                {}, // customData
                "", // subType
                {}, // checkoutData
                [
                    // itemData
                    {
                        item_type: "ad_brand",
                        retailer_network: retailerNetwork,
                        campaign_id: String(campaignId),
                        flight_id: String(flightId),
                        creative_id: String(creativeId),
                        ad_id: String(adId),
                        area,
                        above_fold: isAboveFold,
                    },
                ]
            );
        },
        [retailerNetwork, campaignId, flightId, creativeId, adId, isAboveFold]
    );

    useEffect(() => {
        if (!isLoaded) {
            sendAnalyticsEvent("loaded", "no interaction");
            setIsLoaded(true);
        }
    }, [isLoaded, sendAnalyticsEvent]);

    useEffect(() => {
        if (isVisible) {
            sendKevelTracking(false);
            sendAnalyticsEvent("impression", "no interaction");
        }
    }, [isVisible, sendKevelTracking, sendAnalyticsEvent]);

    function onProductHeroClick(): void {
        sendKevelTracking(true);
        sendAnalyticsEvent("click", "product image");
    }

    function onMainButtonClick(): void {
        sendKevelTracking(true);
        sendAnalyticsEvent("click", "banner");
    }

    // key forces component to re-render on the client for the correct href.
    const { isDomLoaded, isMobileViewport } = useDomViewport();
    const mainButtonKey = isDomLoaded ? "client" : "server";
    const mainButtonHref = isMobileViewport ? link : mobileLink;

    const productCard = (
        <View padding={{ sm: "sm" }} paddingRight={{ sm: "sm", xl: "lg" }}>
            {productDescription && (
                <ProductCardImageEmo lazy={false} src={productImage} alt={productDescription} />
            )}
        </View>
    );

    const retailerHyperlink = (
        <Text
            className={styles.textWithIcon}
            as={"div"}
            color={"secondary"}
            textStyle={"caption-1"}
        >
            <Retailer retailerName={partner} fromSpanStyle={"lowercase"} showLinkIcon={true} />
        </Text>
    );

    return (
        <div ref={containerRef} className={clsx(styles.container)}>
            <View className={styles.partnerProductContainer}>
                <a href={productLink} target="_blank" onClick={onProductHeroClick} rel="noreferrer">
                    {productCard}
                    <View className={styles.productDetailsContainer}>
                        {productPrice && (
                            <Price size={"sm"} inStock={true} fullPrice={productPrice} />
                        )}
                        <Text
                            as={"p"}
                            className={styles.productDescription}
                            textStyle={"caption-1"}
                            color={"secondary"}
                            lineLimit={2}
                            aria-description={gettext("product_card.product_description.label")}
                        >
                            {productDescription}
                        </Text>
                        {retailerHyperlink}
                    </View>
                </a>
            </View>
            <View className={styles.bannerContainer}>
                <a
                    key={mainButtonKey}
                    href={mainButtonHref}
                    className="main-tracked-button"
                    target="_blank"
                    onClick={onMainButtonClick}
                    rel="noreferrer"
                >
                    {/* Note for future changes: If something regarding the media queries in images load or the picture sources change please update `base_page.jinja` as well since we are preloading them conditionally. */}
                    <picture>
                        <source media="(min-width: 576px)" srcSet={jpgBannerImage} />
                        <source media="(max-width: 575px)" srcSet={jpgMpuImage} />
                        <img
                            className={styles.containerImage}
                            src={jpgBannerImage}
                            alt="test banner"
                        />
                    </picture>
                    <View className={styles.partnerLabel}>
                        <Button
                            size={"sm"}
                            icon={"hyperlink"}
                            width={"full"}
                            variant={"tertiary"}
                            title={partner}
                        />
                    </View>
                </a>

                <a
                    className={styles.info}
                    onClick={(): void => sendAnalyticsEvent("click", "icon")}
                >
                    <Button
                        size={"sm"}
                        icon={"info-icon"}
                        width={"full"}
                        variant={"tertiary"}
                        title={gettext("feed.header.sponsored_label")} // Sponsored
                    />
                </a>
            </View>
        </div>
    );
}
